/**
 * OrientationGuard — overlay DOM que orienta o jogador a virar o celular
 * pra paisagem em viewports portrait estreitos.
 *
 * Por quê: o jogo é desenhado num canvas lógico 1280x720 com scale.FIT.
 * Em portrait num celular (~375x812) a área útil renderiza tipo 375x211
 * com barras pretas enormes no topo e embaixo — inutilizável.
 *
 * Heurística: portrait E width < 600px = celular em pé. iPad em portrait
 * (768+) e desktop redimensionado pra estreito não disparam.
 *
 * Estratégia escolhida (vs reescrever o layout pra mobile portrait):
 * preservar 100% do layout PC/tablet existente que já funciona bem, e
 * tratar o phone como caso especial via prompt de rotação. Padrão
 * comum em jogos arcade. Reescrever pra portrait seria bem mais
 * trabalho e ia comprometer a leitura das contas (que precisam de
 * espaço horizontal pra options + timer).
 */

const OVERLAY_ID = 'orientation-guard';
const PHONE_PORTRAIT_WIDTH_MAX = 600;

let listenersAttached = false;

export function installOrientationGuard(): void {
  if (typeof document === 'undefined') return;

  // Idempotente: se o overlay sumiu do DOM (ex: testes recriando o body),
  // a próxima chamada recria. Listeners ficam vinculados uma vez só
  // (state module-level) e olham o DOM atual em cada disparo.
  if (!document.getElementById(OVERLAY_ID)) {
    document.body.appendChild(createOverlay());
  }

  if (!listenersAttached) {
    window.addEventListener('resize', updateOverlayVisibility, { passive: true });
    // orientationchange é mais responsivo em iOS Safari do que esperar
    // o resize disparar depois da animação do sistema.
    window.addEventListener('orientationchange', updateOverlayVisibility, { passive: true });
    listenersAttached = true;
  }

  updateOverlayVisibility();
}

function updateOverlayVisibility(): void {
  const overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) return;
  const isPortraitPhone =
    window.innerWidth < window.innerHeight && window.innerWidth < PHONE_PORTRAIT_WIDTH_MAX;
  overlay.style.display = isPortraitPhone ? 'flex' : 'none';
}

function createOverlay(): HTMLElement {
  const div = document.createElement('div');
  div.id = OVERLAY_ID;
  div.setAttribute('role', 'dialog');
  div.setAttribute('aria-label', 'Vire o celular para o modo paisagem');
  div.style.cssText = `
    position: fixed; inset: 0;
    background: #0b0b1a;
    color: #f0f0ff;
    font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    display: none;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 99999;
    padding:
      max(24px, env(safe-area-inset-top))
      max(24px, env(safe-area-inset-right))
      max(24px, env(safe-area-inset-bottom))
      max(24px, env(safe-area-inset-left));
    box-sizing: border-box;
    text-align: center;
  `;
  div.innerHTML = `
    <div style="font-size: 64px; margin-bottom: 16px; animation: tg-orient-rotate 2.4s ease-in-out infinite;">📱</div>
    <h2 style="font-size: 22px; margin: 0 0 8px 0; font-weight: 700; color: #f0f0ff;">Vire o celular</h2>
    <p style="font-size: 15px; margin: 0; color: #9b9bff; max-width: 320px; line-height: 1.5;">
      O Tabuada Geek roda melhor em modo paisagem.
      Gire o celular pra horizontal pra começar a jogar.
    </p>
    <style>
      @keyframes tg-orient-rotate {
        0%, 100% { transform: rotate(0deg); }
        50%      { transform: rotate(-90deg); }
      }
    </style>
  `;
  return div;
}
